import i18nFactory from '@yandex-int/i18n';
import { groupBy, intersection, mapValues, memoize, reduce, some, without } from 'lodash';

import { RATE_CORRECTION_SWITCH_OFF_VALUE } from '../../constants';
import { ValidationErrorData } from '../../typings/common';
import {
    CloudinessTypes,
    DeviceRateCorrectionType,
    EditorCommonRateCorrectionCondition,
    EditorDemographyRateCorrection,
    EditorDemographyRateCorrectionCondition,
    EditorDeviceRateCorrection,
    EditorDeviceRateCorrectionCondition,
    EditorInventoryRateCorrection,
    EditorInventoryRateCorrectionCondition,
    EditorRateCorrection,
    EditorRetargetingRateCorrectionCondition,
    EditorSmartBannersRateCorrectionCondition,
    EditorTrafficJamRateCorrectionCondition,
    EditorVideoAdditionRateCorrectionCondition,
    EditorWeatherRateCorrectionCondition,
    INVENTORY_AD_TYPE_CORRECTIONS,
    INVENTORY_VIDEO_CORRECTIONS,
    RainfallTypes,
    RateControlMode,
    RateControlValue,
    RateCorrectionAge,
    RateCorrectionConditionChooserItem,
    RateCorrectionConditionChooserItemsGroup,
    RateCorrectionGender,
    RateCorrectionMode,
    RateCorrectionType,
    RatesCorrectionsEditorValue,
    TrafficJamRateCorrectionsType,
    WeatherRateCorrectionCondition,
    WeatherRateCorrectionsType
} from '../../typings/ratesCorrections';
import { getRandomId } from '../IdHelpers/IdHelpers';

import * as keyset from './RateCorrectionsEditorHelpers.i18n';

const i18n = i18nFactory(keyset);

export interface RateCorrectionValidationParams {
    minRate?: number;
    maxRate?: number;
    mode?: RateCorrectionMode;
    minTemperature?: number;
    maxTemperature?: number;
}

export const validateMaxTemperature = (minTemperature: number, maxTemperature: number): ValidationErrorData[] => {
    if (maxTemperature < minTemperature) {
        return [
            {
                code: 'incorrect-max-temperature',
                text: i18n('incorrect-max-temperature')
            }
        ];
    }

    return [];
};

export function validateRateCorrectionValue(
    value: RateControlValue,
    validationParams: RateCorrectionValidationParams
): ValidationErrorData[] {
    const errors: ValidationErrorData[] = [];
    const { minRate, maxRate } = validationParams;

    if (value.value === undefined) {
        errors.push({
            code: 'value-required',
            text: i18n('value-required')
        });
    } else if (value.value < 0 || value.value % 1) {
        errors.push({
            code: 'positive-value',
            text: i18n('positive-value')
        });
    } else if (minRate && getRateValueFromRateControlValue(value) < minRate) {
        errors.push({
            code: 'min-rate-value',
            text: i18n('min-rate-value', { value: Math.abs(minRate) })
        });
    } else if (maxRate && getRateValueFromRateControlValue(value) > maxRate) {
        errors.push({
            code: 'max-rate-value',
            text: i18n('max-rate-value', {
                value: maxRate
            })
        });
    }

    return errors;
}

export function validateRateCorrectionAge(age?: RateCorrectionAge): ValidationErrorData[] {
    const errors: ValidationErrorData[] = [];

    if (age === undefined) {
        errors.push({
            code: 'age-required',
            text: i18n('age-required')
        });
    }

    return errors;
}

export function validateRateCorrectionGender(gender?: RateCorrectionGender): ValidationErrorData[] {
    const errors: ValidationErrorData[] = [];

    if (gender === undefined) {
        errors.push({
            code: 'gender-required',
            text: i18n('age-required')
        });
    }

    return errors;
}

export function getRateValueFromRateControlValue(rateControlValue: RateControlValue): number {
    if (rateControlValue.value === undefined) {
        throw Error(
            'Ошибка пребразования RateControlValue к численному значению: rateControlValue.value должно быть определено'
        );
    }

    if (rateControlValue.mode === RateControlMode.down) {
        return -rateControlValue.value;
    }

    return rateControlValue.value;
}

export const validateCommonRateCorrectionItem = (
    correctionItem: EditorCommonRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => {
    const errors: Record<string, ValidationErrorData[]> = {};

    errors.value = validateRateCorrectionValue(correctionItem.value, validationParams);

    return errors;
};

export const validateInventoryRateCorrectionItem = (
    correctionItem: EditorInventoryRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => validateCommonRateCorrectionItem(correctionItem, validationParams);

export const validateDeviceRateCorrectionItem = (
    correctionItem: EditorDeviceRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => validateCommonRateCorrectionItem(correctionItem, validationParams);

export const validateRetargetingRateCorrectionItem = (
    correctionItem: EditorRetargetingRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => validateCommonRateCorrectionItem(correctionItem, validationParams);

export const validateVideoAdditionRateCorrectionItem = (
    correctionItem: EditorVideoAdditionRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => validateCommonRateCorrectionItem(correctionItem, validationParams);

export const validateSmartBannersRateCorrectionItem = (
    correctionItem: EditorSmartBannersRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => validateCommonRateCorrectionItem(correctionItem, validationParams);

export const validateWeatherRateCorrectionItem = (
    correctionItem: EditorWeatherRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => {
    const errors: Record<string, ValidationErrorData[]> = {};

    if (validationParams.mode === RateCorrectionMode.oneDone) {
        errors.value = validateRateCorrectionValue(correctionItem.value, validationParams);
    }

    if (correctionItem.weatherType === WeatherRateCorrectionsType.temperature) {
        errors.maxTemperature = validateMaxTemperature(correctionItem.minTemperature, correctionItem.maxTemperature);
    }

    return errors;
};

export const validateTrafficRateCorrectionItem = (
    correctionItem: EditorTrafficJamRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => validateCommonRateCorrectionItem(correctionItem, validationParams);

export const getNextValueForWeatherRateCorrection = (
    newItem: EditorWeatherRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): EditorWeatherRateCorrectionCondition => {
    switch (newItem.weatherType) {
        case WeatherRateCorrectionsType.cloudiness:
        case WeatherRateCorrectionsType.rainfall:
            return {
                ...newItem
            };
        case WeatherRateCorrectionsType.temperature:
            return {
                ...newItem,
                minTemperature: getNextValueMinTemperatureForRateCorrection(
                    newItem.minTemperature,
                    validationParams.minTemperature
                ),
                maxTemperature: getNextValueMaxTemperatureForRateCorrection(
                    newItem.maxTemperature,
                    validationParams.maxTemperature
                )
            };
        default:
            throw new Error('Неподдерживаемый тип погоды');
    }
};

export const getNextValueMinTemperatureForRateCorrection = (
    minTemperature?: number,
    minTemperatureLimit?: number
): number => {
    if (!minTemperatureLimit) {
        throw new Error('Не задано константное значение ограничения минимальной температуры корректировки ставки');
    }

    if (minTemperature === undefined) {
        return minTemperatureLimit;
    }

    return minTemperature < minTemperatureLimit ? minTemperatureLimit : minTemperature;
};

export const getNextValueMaxTemperatureForRateCorrection = (
    maxTemperature?: number,
    maxTemperatureLimit?: number
): number => {
    if (!maxTemperatureLimit) {
        throw new Error('Не задано константное значение ограничения маскисмальной температуры корректировки ставки');
    }

    if (maxTemperature === undefined) {
        return maxTemperatureLimit;
    }

    return maxTemperature > maxTemperatureLimit ? maxTemperatureLimit : maxTemperature;
};

export const validateDemographyRateCorrectionItem = (
    correctionItem: EditorDemographyRateCorrectionCondition,
    validationParams: RateCorrectionValidationParams
): Record<string, ValidationErrorData[]> => {
    const errors = validateCommonRateCorrectionItem(correctionItem, validationParams);

    errors.age = validateRateCorrectionAge(correctionItem.age);

    errors.gender = validateRateCorrectionGender(correctionItem.gender);

    return errors;
};

export const isCorrectionValid = (validationData: RateCorrectionValidationData): boolean => {
    const hasItemsErrors = some(validationData.items, item => {
        return some(item, error => error.length !== 0);
    });
    const hasGeneralErrors = validationData.general.length !== 0;

    const hasSingleMassRateControlErrors = validationData.errorsSingleMassRateControl.length > 0;

    return !hasItemsErrors && !hasGeneralErrors && !hasSingleMassRateControlErrors;
};

export const getRateCorrectionGeneralErrors = (correction: EditorRateCorrection): Array<ValidationErrorData> => {
    switch (correction.type) {
        case RateCorrectionType.demography:
            return validateDemographyRateCorrection(correction);

        case RateCorrectionType.device:
            return validateDeviceRateCorrection(correction);

        case RateCorrectionType.inventory:
            return validateInventoryRateCorrection(correction);

        default:
            return [];
    }
};

export const validateSingleMassRateValueForRateCorrection = (
    correction: EditorRateCorrection,
    validationParams: RateCorrectionValidationParams
): Array<ValidationErrorData> => {
    const { singleMassRateControlValue, mode } = correction;
    const { minRate, maxRate } = validationParams;

    if (mode === RateCorrectionMode.oneDone || mode === undefined || singleMassRateControlValue === undefined) {
        return [];
    }

    if (singleMassRateControlValue.value === undefined) {
        return [
            {
                code: 'single-mass-rate-empty',
                text: i18n('single-mass-rate-empty')
            }
        ];
    }

    // проверка на не отрицательное не дробное число
    if (singleMassRateControlValue.value < 0 || singleMassRateControlValue.value % 1) {
        return [
            {
                code: 'positive-value',
                text: i18n('positive-value')
            }
        ];
    }

    if (minRate && getRateValueFromRateControlValue(singleMassRateControlValue) < minRate) {
        return [
            {
                code: 'min-rate-value',
                text: i18n('min-rate-value', { value: Math.abs(minRate) })
            }
        ];
    }

    if (maxRate && getRateValueFromRateControlValue(singleMassRateControlValue) > maxRate) {
        return [
            {
                code: 'max-rate-value',
                text: i18n('max-rate-value', {
                    value: maxRate
                })
            }
        ];
    }

    return [];
};

export const validateDemographyRateCorrection = (
    demographyCorrection: EditorDemographyRateCorrection
): Array<ValidationErrorData> => {
    const items = demographyCorrection.items;

    const equalItemsGroups = items.reduce(
        (res: Record<string, Array<number>>, item: EditorDemographyRateCorrectionCondition, index: number) => {
            if (!item.age || !item.gender) {
                return res;
            }

            const groupKeys: Array<string> = [];

            const ageKeys =
                item.age === RateCorrectionAge.all
                    ? without(Object.keys(RateCorrectionAge).map(key => RateCorrectionAge[key]), RateCorrectionAge.all)
                    : [item.age];

            const genderKeys =
                item.gender === RateCorrectionGender.all
                    ? without(
                          Object.keys(RateCorrectionGender).map(key => RateCorrectionGender[key]),
                          RateCorrectionGender.all
                      )
                    : [item.gender];

            ageKeys.forEach(ageValue => {
                genderKeys.forEach(genderValue => {
                    groupKeys.push(ageValue + genderValue);
                });
            });

            groupKeys.forEach(groupKey => {
                if (res[groupKey] === undefined) {
                    res[groupKey] = [];
                }

                res[groupKey].push(index + 1);
            });

            return res;
        },
        {}
    );

    const groupedEqualItems = groupBy(equalItemsGroups, (indexes: Array<number>) => {
        return indexes.sort().join(', ');
    });

    const errors = reduce(
        groupedEqualItems,
        (res: Array<ValidationErrorData>, value: Array<Array<number>>, key: string) => {
            if (value[0].length < 2) {
                return res;
            }

            res.push({
                code: 'equal-items',
                text: i18n('equal-items', {
                    numbers: key
                })
            });

            return res;
        },
        []
    );

    return errors;
};

export const validateDeviceRateCorrection = (
    deviceCorrection: EditorDeviceRateCorrection
): Array<ValidationErrorData> => {
    const errors: Array<ValidationErrorData> = [];
    const items = deviceCorrection.items;
    const allDevicesTypes = [DeviceRateCorrectionType.allMobile, DeviceRateCorrectionType.desktops];
    const areAllDevicesSwitchedOff =
        items.filter(item => {
            return (
                allDevicesTypes.includes(item.deviceType) &&
                item.value.value !== undefined &&
                getRateValueFromRateControlValue(item.value) === RATE_CORRECTION_SWITCH_OFF_VALUE
            );
        }).length === allDevicesTypes.length;

    if (areAllDevicesSwitchedOff) {
        errors.push({
            code: 'all-devices-rate-value',
            text: i18n('all-devices-rate-value', {
                value: RATE_CORRECTION_SWITCH_OFF_VALUE
            })
        });
    }

    return errors;
};

export const validateInventoryRateCorrection = (
    correction: EditorInventoryRateCorrection
): Array<ValidationErrorData> => {
    const errors: Array<ValidationErrorData> = [];
    const items = correction.items;
    const allInventoryTypes = INVENTORY_AD_TYPE_CORRECTIONS.concat(INVENTORY_VIDEO_CORRECTIONS);
    const areAllSwitchedOff =
        items.filter(item => {
            return (
                allInventoryTypes.includes(item.inventoryType) &&
                item.value.value !== undefined &&
                getRateValueFromRateControlValue(item.value) === RATE_CORRECTION_SWITCH_OFF_VALUE
            );
        }).length === allInventoryTypes.length;

    if (areAllSwitchedOff) {
        errors.push({
            code: 'all-inventories-rate-value',
            text: i18n('all-inventories-rate-value', {
                value: RATE_CORRECTION_SWITCH_OFF_VALUE
            })
        });
    }

    return errors;
};

export interface RateCorrectionValidationData {
    items: Record<string, Record<string, ValidationErrorData[]>>;
    general: ValidationErrorData[];
    errorsSingleMassRateControl: ValidationErrorData[];
}

export interface RateCorrectionValidationResult extends RateCorrectionValidationData {
    isValid: boolean;
}

export type RateCorrectionEditorValueValidationResult = {
    errors: Record<RateCorrectionType, RateCorrectionValidationResult>;
    isValid: boolean;
};

export const validateRateCorrectionEditor = (
    editorStateValue: RatesCorrectionsEditorValue
): RateCorrectionEditorValueValidationResult => {
    let isEditorValueValid = true;
    const errors: Record<RateCorrectionType, RateCorrectionValidationResult> = mapValues(
        editorStateValue,
        (correction: EditorRateCorrection, key: RateCorrectionType) => {
            const validationParams = {
                maxRate: correction.maxRate,
                minRate: correction.minRate
            };
            let itemsValidationResult: Record<string, Record<string, ValidationErrorData[]>>;

            switch (correction.type) {
                case RateCorrectionType.retargeting: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateRetargetingRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                case RateCorrectionType.device: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateDeviceRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                case RateCorrectionType.inventory: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateInventoryRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                case RateCorrectionType.demography: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateDemographyRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                case RateCorrectionType.videoAddition: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateVideoAdditionRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                case RateCorrectionType.smartBanners: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateSmartBannersRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                case RateCorrectionType.weather: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateWeatherRateCorrectionItem(item, {
                            ...validationParams,
                            mode: correction.mode
                        });

                        return res;
                    }, {});

                    const correctionItemsWithIntersectionErrors = applyForWeatherRateCorrectionsItemsIntersectionErrors(
                        correction.items,
                        correction.mode
                    );

                    correctionItemsWithIntersectionErrors.forEach(correctionItem => {
                        const intersectionErrors = correctionItem.errors.intersectionItem;

                        if (intersectionErrors && intersectionErrors.length > 0) {
                            itemsValidationResult[correctionItem.id].intersectionItem = intersectionErrors;
                        }
                    });

                    break;
                }

                case RateCorrectionType.trafficJam: {
                    itemsValidationResult = correction.items.reduce((res, item) => {
                        res[item.id] = validateTrafficRateCorrectionItem(item, validationParams);

                        return res;
                    }, {});

                    break;
                }

                default:
                    throw Error('Неподдерживаемый тип корректировки');
            }

            const validationResult = {
                items: itemsValidationResult,
                general: getRateCorrectionGeneralErrors(correction),
                errorsSingleMassRateControl: validateSingleMassRateValueForRateCorrection(correction, validationParams)
            };

            const isValid = isCorrectionValid(validationResult);

            if (isValid === false) {
                isEditorValueValid = false;
            }

            return {
                ...validationResult,
                isValid
            };
        }
    );

    return {
        isValid: isEditorValueValid,
        errors
    };
};

export const getAllSelectableChooserItemsLength = memoize(
    (chooserData: Array<RateCorrectionConditionChooserItemsGroup>): number =>
        chooserData.reduce((res: number, group: RateCorrectionConditionChooserItemsGroup) => {
            if (group.hasMultiChoice) {
                res += group.items.length;
            } else {
                res++;
            }

            return res;
        }, 0)
);

export const isRateCorrectionItemEnabledForChange = (
    key: string,
    chooserData: Array<RateCorrectionConditionChooserItemsGroup>,
    selectedItemsIdsLength: number
): boolean => {
    const itemGroup = chooserData.find(itemsGroup => {
        return itemsGroup.items.some(item => item.id === key);
    });

    if (!itemGroup) {
        throw new Error(`${key} does not located in rate selection list`);
    }

    const allSelectableChooserItemsLength = getAllSelectableChooserItemsLength(chooserData);

    if (itemGroup && itemGroup.hasMultiChoice) {
        return selectedItemsIdsLength !== allSelectableChooserItemsLength;
    } else {
        return Boolean(itemGroup && itemGroup.items.length > 1);
    }
};

export const weatherRateCorrectionsChooserDataTitleByType = {
    [WeatherRateCorrectionsType.temperature]: i18n('temperature'),
    [WeatherRateCorrectionsType.rainfall]: i18n('rainfall'),
    [WeatherRateCorrectionsType.cloudiness]: i18n('cloudiness')
};

export const getWeatherChooserData = (): Array<RateCorrectionConditionChooserItem> => {
    return Object.keys(weatherRateCorrectionsChooserDataTitleByType).map(key => {
        return {
            id: key,
            name: weatherRateCorrectionsChooserDataTitleByType[key]
        };
    });
};

export const getDefaultValueWeatherRateCorrectionConditionByType = (
    weatherType: WeatherRateCorrectionsType,
    validationParams: RateCorrectionValidationParams
): EditorWeatherRateCorrectionCondition => {
    const commonDefaultCondition = {
        id: getRandomId(),
        value: { mode: RateControlMode.up },
        errors: {}
    };

    switch (weatherType) {
        case WeatherRateCorrectionsType.cloudiness:
            return {
                ...commonDefaultCondition,
                weatherType,
                cloudinessIds: [CloudinessTypes.clear]
            };

        case WeatherRateCorrectionsType.rainfall:
            return {
                ...commonDefaultCondition,
                weatherType,
                rainfallIds: [RainfallTypes.none]
            };

        case WeatherRateCorrectionsType.temperature:
            if (validationParams.minTemperature === undefined || validationParams.maxTemperature === undefined) {
                throw new Error('Не задано константное значение ограничения температуры корректировки ставки');
            }

            return {
                ...commonDefaultCondition,
                weatherType,
                minTemperature: validationParams.minTemperature,
                maxTemperature: validationParams.maxTemperature
            };

        default:
            throw new Error(`Unsupported weather type: ${weatherType}`);
    }
};

export const cloudinessWeatherTitleByType = {
    [CloudinessTypes.clear]: i18n('cloudiness-clear'),
    [CloudinessTypes.partly]: i18n('cloudiness-partly'),
    [CloudinessTypes.medium]: i18n('cloudiness-medium'),
    [CloudinessTypes.significant]: i18n('cloudiness-significant'),
    [CloudinessTypes.overcast]: i18n('cloudiness-overcast')
};

export const cloudinessTypesByValue = {
    0: CloudinessTypes.clear,
    25: CloudinessTypes.partly,
    50: CloudinessTypes.medium,
    75: CloudinessTypes.significant,
    100: CloudinessTypes.overcast
};

export const cloudinessValuesByType = {
    [CloudinessTypes.clear]: 0,
    [CloudinessTypes.partly]: 25,
    [CloudinessTypes.medium]: 50,
    [CloudinessTypes.significant]: 75,
    [CloudinessTypes.overcast]: 100
};

export const trafficValuesByType = {
    [TrafficJamRateCorrectionsType.free]: '0',
    [TrafficJamRateCorrectionsType.light]: '1',
    [TrafficJamRateCorrectionsType.hard]: '2',
    [TrafficJamRateCorrectionsType.veryHard]: '3'
};

export const getWeatherCloudinessTitles = (): Array<RateCorrectionConditionChooserItem> => {
    return Object.keys(cloudinessWeatherTitleByType).map(key => {
        return {
            id: key,
            name: cloudinessWeatherTitleByType[key]
        };
    });
};

export const rainfallWeatherTitleByType = {
    [RainfallTypes.none]: i18n('rainfall-none'),
    [RainfallTypes.light]: i18n('rainfall-light'),
    [RainfallTypes.average]: i18n('rainfall-average'),
    [RainfallTypes.heavy]: i18n('rainfall-heavy'),
    [RainfallTypes.rainSnow]: i18n('rainfall-rain-snow')
};

export const rateCorrectionModeTitleByModeType = {
    [RateCorrectionMode.allDone]: i18n('all-conditions-done'),
    [RateCorrectionMode.oneDone]: i18n('one-condition-done')
};

export const rainfallTypesByValue = {
    0: RainfallTypes.none,
    25: RainfallTypes.light,
    50: RainfallTypes.average,
    75: RainfallTypes.heavy,
    100: RainfallTypes.rainSnow
};

export const rainfallValuesByType = {
    [RainfallTypes.none]: 0,
    [RainfallTypes.light]: 25,
    [RainfallTypes.average]: 50,
    [RainfallTypes.heavy]: 75,
    [RainfallTypes.rainSnow]: 100
};

export const applyForWeatherRateCorrectionsItemsIntersectionErrors = (
    items: EditorWeatherRateCorrectionCondition[],
    mode?: RateCorrectionMode
): EditorWeatherRateCorrectionCondition[] => {
    return items.map((item, index) => {
        const intersectionList: number[] = [];

        items.forEach((compareItem, compareIndex) => {
            if (index !== compareIndex && isIntersectionWeatherItems(item, compareItem, mode)) {
                intersectionList.push(compareIndex + 1);
            }
        });

        let newItem;

        if (intersectionList.length > 0) {
            newItem = {
                ...item,
                errors: {
                    ...item.errors,
                    intersectionItem: [
                        {
                            code: 'intersection-items-warning',
                            text: i18n('intersection-items-warning', {
                                count: intersectionList.length,
                                list: intersectionList.join(', ')
                            })
                        }
                    ]
                }
            };
        } else {
            newItem = {
                ...item,
                errors: {
                    ...item.errors,
                    intersectionItem: []
                }
            };
        }

        return newItem;
    });
};

export const isIntersectionWeatherItems = (
    item: EditorWeatherRateCorrectionCondition,
    compareItem: EditorWeatherRateCorrectionCondition,
    mode?: RateCorrectionMode
): boolean => {
    if (compareItem.weatherType !== item.weatherType) {
        return false;
    }

    if (mode === RateCorrectionMode.allDone) {
        return true;
    }

    switch (item.weatherType) {
        case WeatherRateCorrectionsType.rainfall:
            // условие нужно так как TS не понимает, что здесь compareItem.weatherType === rainfall
            if (compareItem.weatherType !== WeatherRateCorrectionsType.rainfall) {
                return false;
            }

            return intersection(item.rainfallIds, compareItem.rainfallIds).length > 0;
        case WeatherRateCorrectionsType.cloudiness:
            // условие нужно так как TS не понимает, что здесь compareItem.weatherType === cloudiness
            if (compareItem.weatherType !== WeatherRateCorrectionsType.cloudiness) {
                return false;
            }

            return intersection(item.cloudinessIds, compareItem.cloudinessIds).length > 0;
        case WeatherRateCorrectionsType.temperature:
            // условие нужно так как TS не понимает, что здесь compareItem.weatherType === temperature
            if (compareItem.weatherType !== WeatherRateCorrectionsType.temperature) {
                return false;
            }

            return (
                (compareItem.minTemperature >= item.minTemperature &&
                    compareItem.minTemperature <= item.maxTemperature) ||
                (compareItem.maxTemperature >= item.minTemperature &&
                    compareItem.maxTemperature <= item.maxTemperature) ||
                (item.minTemperature >= compareItem.minTemperature &&
                    item.minTemperature <= compareItem.maxTemperature) ||
                (item.maxTemperature >= compareItem.minTemperature && item.maxTemperature <= compareItem.maxTemperature)
            );

        default:
            throw new Error('Неподдерживаемый тип погоды');
    }
};

export const getTitleWeatherError = (item: EditorWeatherRateCorrectionCondition, field: string): string | undefined => {
    let tooltipErrorText;
    const errorsList = item.errors[field];

    if (errorsList && errorsList.length > 0) {
        tooltipErrorText = errorsList[0].text as string;
    }

    return tooltipErrorText;
};

export const getHintDescriptionForWeatherRateCorrectionItem = (item: WeatherRateCorrectionCondition): string => {
    switch (item.weatherType) {
        case WeatherRateCorrectionsType.temperature:
            return i18n('temperature-hint', {
                minTemperature: item.minTemperature,
                maxTemperature: item.maxTemperature
            });
        case WeatherRateCorrectionsType.cloudiness:
            return i18n('cloudiness-hint', {
                value: item.cloudinessIds.map(cloudinessId => cloudinessWeatherTitleByType[cloudinessId]).join(', ')
            });
        case WeatherRateCorrectionsType.rainfall:
            return i18n('rainfall-hint', {
                value: item.rainfallIds.map(rainfallId => rainfallWeatherTitleByType[rainfallId]).join(', ')
            });
        default:
            throw new Error('Неподдерживаемый тип погоды');
    }
};

export const trafficJamRateCorrectionsChooserDataTitleByType = {
    [TrafficJamRateCorrectionsType.free]: i18n('traffic-jam-free'),
    [TrafficJamRateCorrectionsType.light]: i18n('traffic-jam-light'),
    [TrafficJamRateCorrectionsType.hard]: i18n('traffic-jam-hard'),
    [TrafficJamRateCorrectionsType.veryHard]: i18n('traffic-jam-very-hard')
};

export const getTrafficJamChooserData = (): Array<RateCorrectionConditionChooserItem> => {
    return Object.keys(trafficJamRateCorrectionsChooserDataTitleByType).map(key => {
        return {
            id: key,
            name: trafficJamRateCorrectionsChooserDataTitleByType[key]
        };
    });
};
